"use client";

import { Control, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { format, parseISO } from "date-fns";

import { Badge } from "@/components/ui/badge";
import { getCurrencySymbol, PriceCurrency } from "@/features/calendar/types/pricing";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale";

interface SummaryStepProps {
  control: Control<any>;
}

export function SummaryStep({ control }: SummaryStepProps) {
  const { t, i18n } = useTranslation(["finances", "common", "workspaces"]);

  const formValues = useWatch({ control });
  const {
    type: selectedType,
    amount: selectedAmount,
    currency: selectedCurrency,
    // payer_user_id included but not displayed by id
    lesson_id: selectedLesson,
    payment_due,
    should_notify_payer,
    description,
    notification_message,
    payment_destination,
    payer_user_name,
    // payer_avatar_url not used in summary for now
    lesson_topic,
    lesson_start_time,
    lesson_subject_name,
  } = formValues;

  const selectedStudentName = payer_user_name;
  const selectedLessonTopic = lesson_topic;

  return (
    <div className="flex h-full flex-col px-6 py-8 max-sm:px-4 max-sm:py-6">
      {/* Header */}
      <div className="mb-8 text-center max-sm:mb-6">
        <h1 className="text-accent-foreground mb-3 flex items-center justify-center gap-3 text-2xl font-bold max-sm:text-xl">
          <Icon
            className="text-muted-foreground"
            height="1.75rem"
            icon="tabler:clipboard-check"
            width="1.75rem"
          />
          {t("finances:createPayment.summary")}
        </h1>
        <p className="text-muted-foreground mx-auto max-w-md text-base max-sm:text-sm">
          {t("finances:createPayment.summaryDesc")}
        </p>
      </div>

      {/* Summary Content */}
      <div className="mb-12 flex flex-1 justify-center">
        <div className="w-full max-w-3xl space-y-4">
          {/* Payment Type */}
          <div className="bg-muted/20 flex items-center justify-between rounded-xl p-4 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <div className="bg-muted/30 flex h-10 w-10 items-center justify-center rounded-full">
                <Icon
                  className="text-muted-foreground"
                  height="1.25rem"
                  icon={selectedType === "lesson" ? "tabler:book" : "tabler:receipt"}
                  width="1.25rem"
                />
              </div>
              <span className="text-accent-foreground text-base font-medium">
                {t("finances:createPayment.typeLabel")}
              </span>
            </div>
            <Badge className="bg-accent-foreground text-foreground text-sm">
              {selectedType === "lesson"
                ? t("finances:createPayment.lesson")
                : t("finances:paymentsHistory.other")}
            </Badge>
          </div>

          {/* Amount */}
          <div className="bg-muted/20 flex items-center justify-between rounded-xl p-4 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <div className="bg-muted/30 flex h-10 w-10 items-center justify-center rounded-full">
                <Icon
                  className="text-muted-foreground"
                  height="1.25rem"
                  icon="tabler:currency-dollar"
                  width="1.25rem"
                />
              </div>
              <span className="text-accent-foreground text-base font-medium">
                {t("finances:createPayment.amount")}
              </span>
            </div>
            <div className="text-right">
              <div className="text-accent-foreground text-xl font-semibold">
                {getCurrencySymbol(selectedCurrency as PriceCurrency)}
                {selectedAmount || "0.00"}
              </div>
            </div>
          </div>

          {/* Student */}
          <div className="bg-muted/20 flex items-center justify-between rounded-xl p-4 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <div className="bg-muted/30 flex h-10 w-10 items-center justify-center rounded-full">
                <Icon
                  className="text-muted-foreground"
                  height="1.25rem"
                  icon="tabler:user"
                  width="1.25rem"
                />
              </div>
              <span className="text-accent-foreground text-base font-medium">
                {t("finances:createPayment.studentLabel")}
              </span>
            </div>
            <div className="text-right">
              <div className="text-accent-foreground text-base font-semibold">
                {selectedStudentName || t("common:notSelected")}
              </div>
            </div>
          </div>

          {/* Lesson (if applicable) */}
          {selectedType === "lesson" && selectedLesson && (
            <div className="bg-muted/20 rounded-xl p-4 backdrop-blur-sm">
              <div className="flex items-start gap-3">
                <div className="bg-muted/30 flex h-10 w-10 items-center justify-center rounded-full">
                  <Icon
                    className="text-muted-foreground"
                    height="1.25rem"
                    icon="tabler:book"
                    width="1.25rem"
                  />
                </div>
                <div className="flex-1">
                  <span className="text-accent-foreground mb-2 block text-base font-medium">
                    {t("finances:createPayment.lessonLabel")}
                  </span>
                  <div className="text-accent-foreground text-base font-semibold">
                    {selectedLessonTopic || t("workspaces:untitledLesson")}
                  </div>
                  {lesson_subject_name && (
                    <div className="text-muted-foreground mt-1 text-sm">{lesson_subject_name}</div>
                  )}
                  {lesson_start_time && (
                    <div className="text-muted-foreground mt-1 text-sm">
                      {format(parseISO(lesson_start_time), "PPP", {
                        locale: getDateFnsLocale(i18n.language),
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Description (if other type) */}
          {selectedType === "other" && description && (
            <div className="bg-muted/20 rounded-xl p-4 backdrop-blur-sm">
              <div className="flex items-start gap-3">
                <div className="bg-muted/30 flex h-10 w-10 items-center justify-center rounded-full">
                  <Icon
                    className="text-muted-foreground"
                    height="1.25rem"
                    icon="tabler:file-text"
                    width="1.25rem"
                  />
                </div>
                <div className="flex-1">
                  <span className="text-accent-foreground mb-2 block text-base font-medium">
                    {t("finances:createPayment.description")}
                  </span>
                  <div className="text-accent-foreground text-sm leading-relaxed">
                    {description}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Due Date (if set) */}
          {payment_due && (
            <div className="bg-muted/20 flex items-center justify-between rounded-xl p-4 backdrop-blur-sm">
              <div className="flex items-center gap-3">
                <div className="bg-muted/30 flex h-10 w-10 items-center justify-center rounded-full">
                  <Icon
                    className="text-muted-foreground"
                    height="1.25rem"
                    icon="tabler:calendar"
                    width="1.25rem"
                  />
                </div>
                <span className="text-accent-foreground text-base font-medium">
                  {t("finances:createPayment.dueDate")}
                </span>
              </div>
              <div className="text-right">
                <div className="text-accent-foreground text-base font-semibold">
                  {format(parseISO(`${payment_due}T00:00:00`), "PPP", {
                    locale: getDateFnsLocale(i18n.language),
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Payment Destination (if set) */}
          {payment_destination && (
            <div className="bg-muted/20 flex items-center justify-between rounded-xl p-4 backdrop-blur-sm">
              <div className="flex items-center gap-3">
                <div className="bg-muted/30 flex h-10 w-10 items-center justify-center rounded-full">
                  <Icon
                    className="text-muted-foreground"
                    height="1.25rem"
                    icon="tabler:credit-card"
                    width="1.25rem"
                  />
                </div>

                <div className="flex-1">
                  <span className="text-accent-foreground text-base font-medium">
                    {t("finances:createPayment.destination")}
                  </span>
                  <div className="text-accent-foreground text-sm leading-relaxed">
                    {payment_destination}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notification Settings */}
          <div className="bg-muted/20 flex items-center justify-between rounded-xl p-4 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <div className="bg-muted/30 flex h-10 w-10 items-center justify-center rounded-full">
                <Icon
                  className="text-muted-foreground"
                  height="1.25rem"
                  icon="tabler:bell"
                  width="1.25rem"
                />
              </div>
              <span className="text-accent-foreground text-base font-medium">
                {t("finances:createPayment.sendNotification")}
              </span>
            </div>
            <Badge
              className={`text-sm ${
                should_notify_payer
                  ? "border-emerald-500/30 bg-emerald-500/20 text-emerald-400"
                  : "bg-muted/30 text-muted-foreground"
              }`}
            >
              {should_notify_payer
                ? t("finances:createPayment.yes")
                : t("finances:createPayment.no")}
            </Badge>
          </div>

          {/* Custom Message (if set) */}
          {notification_message && (
            <div className="bg-muted/20 rounded-xl p-4 backdrop-blur-sm">
              <div className="flex items-start gap-3">
                <div className="bg-muted/30 flex h-10 w-10 items-center justify-center rounded-full">
                  <Icon
                    className="text-muted-foreground"
                    height="1.25rem"
                    icon="tabler:message"
                    width="1.25rem"
                  />
                </div>
                <div className="flex-1">
                  <span className="text-accent-foreground mb-2 block text-base font-medium">
                    {t("finances:createPayment.customMessage")}
                  </span>
                  <div className="text-accent-foreground text-sm leading-relaxed">
                    {notification_message}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
